import React, { useState, useRef } from "react";

const REPO_RE = /^[a-zA-Z0-9._-]+\/[a-zA-Z0-9._-]+$/;

export default function ExistingApiUploadCard({ data, sessionId, backendUrl, onSubmit }) {
  const hasJar    = data.accepts.includes("jar");
  const hasGithub = data.accepts.includes("github");
  const showTabs  = hasJar && hasGithub;

  const [tab, setTab]           = useState(hasJar ? "jar" : "github");
  const [file, setFile]         = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");

  const [repo, setRepo]     = useState("");
  const [branch, setBranch] = useState("main");
  const [token, setToken]   = useState("");
  const [showToken, setShowToken] = useState(false);
  const [repoErr, setRepoErr]   = useState("");

  const inputRef = useRef();

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) pickFile(dropped);
  };

  const pickFile = (f) => {
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["jar", "zip"].includes(ext)) {
      setUploadErr("Only .jar or .zip files are accepted.");
      return;
    }
    setFile(f);
    setUploadErr("");
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setUploadErr("");
    try {
      const form = new FormData();
      form.append("sessionId", sessionId);
      form.append("file", file);
      const res = await fetch(backendUrl + data.uploadEndpoint, {
        method: "POST",
        headers: { "x-access-token": localStorage.getItem("token") || "" },
        body: form,
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || "Upload failed");
      onSubmit({ type: "file", projectPath: json.projectPath });
    } catch (err) {
      setUploadErr(err.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleGithubSubmit = () => {
    if (!REPO_RE.test(repo)) {
      setRepoErr("Enter a valid repo in owner/repo format.");
      return;
    }
    setRepoErr("");
    onSubmit({ type: "github", repo, branch: branch || "main", token: token || undefined });
  };

  return (
    <div className="sh-existing-api-card">
      {showTabs && (
        <div className="sh-existing-api-tabs">
          <button
            className={`sh-existing-api-tab${tab === "jar" ? " active" : ""}`}
            onClick={() => setTab("jar")}
          >
            📁 Upload JAR / ZIP
          </button>
          <button
            className={`sh-existing-api-tab${tab === "github" ? " active" : ""}`}
            onClick={() => setTab("github")}
          >
            🔗 GitHub Repo
          </button>
        </div>
      )}

      {(tab === "jar" && hasJar) && (
        <div>
          <div
            className={`sh-existing-api-dropzone${dragOver ? " drag-over" : ""}`}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            {file
              ? <span>📦 {file.name} <span style={{ opacity: 0.6, fontSize: 11 }}>({(file.size / 1024).toFixed(1)} KB)</span></span>
              : <span>Drop <strong>.jar</strong> or <strong>.zip</strong> here, or click to browse</span>
            }
          </div>
          <input
            ref={inputRef}
            type="file"
            accept=".jar,.zip"
            style={{ display: "none" }}
            onChange={(e) => { if (e.target.files[0]) pickFile(e.target.files[0]); }}
          />
          {uploadErr && <div className="sh-existing-api-error">{uploadErr}</div>}
          <div className="sh-existing-api-actions">
            <button
              className="sh-existing-api-btn-primary"
              disabled={!file || uploading}
              onClick={handleUpload}
            >
              {uploading ? "Uploading…" : "Upload"}
            </button>
          </div>
        </div>
      )}

      {(tab === "github" && hasGithub) && (
        <div>
          <div className="sh-existing-api-field">
            <label>Repo</label>
            <input
              value={repo}
              onChange={(e) => { setRepo(e.target.value); setRepoErr(""); }}
              placeholder="owner/repo-name"
            />
          </div>
          <div className="sh-existing-api-field">
            <label>Branch</label>
            <input
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
              placeholder="main"
            />
          </div>
          <div className="sh-existing-api-field">
            <label>Token</label>
            <div style={{ position: "relative", flex: 1 }}>
              <input
                type={showToken ? "text" : "password"}
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Leave blank for public repos"
                style={{ width: "100%", paddingRight: 32 }}
              />
              <button
                className="sh-existing-api-eye"
                onClick={() => setShowToken(v => !v)}
                title={showToken ? "Hide" : "Show"}
              >
                {showToken ? "🙈" : "👁"}
              </button>
            </div>
          </div>
          {repoErr && <div className="sh-existing-api-error">{repoErr}</div>}
          <div className="sh-existing-api-actions">
            <button
              className="sh-existing-api-btn-primary"
              disabled={!repo}
              onClick={handleGithubSubmit}
            >
              Use this repo
            </button>
          </div>
        </div>
      )}

      {data.canSkip && (
        <div style={{ marginTop: 10, textAlign: "center" }}>
          <button className="sh-existing-api-skip" onClick={() => onSubmit({ type: "skip" })}>
            Skip — {data.skipHint}
          </button>
        </div>
      )}
    </div>
  );
}
